import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Added import
import { IncludedTranscripts } from '@components/interview/analysis-components/IncludeTranscripts';
import { Metadata } from '@components/interview/analysis-components/MetaData';
import { PriorityInsights } from '@components/interview/analysis-components/ProrityInsight';
import { Summary } from '@components/interview/analysis-components/Summary';
import CampaignIcon from '@mui/icons-material/Campaign'; // Strategy
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes'; // Discourse/Frame
import StarIcon from '@mui/icons-material/Star'; // Dominant Frame
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; // Quality Notes
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { AnalysisLayout } from './layout';

// Helper to safely parse JSON results
const safeParseResults = (results) => {
  if (!results) return { data: null, error: 'Results data is missing.' };
  try {
    const parsedData = typeof results === 'string' ? JSON.parse(results) : results;
    return { data: parsedData, error: null };
  } catch (error) {
    console.error('Error parsing FramingDiscourseAnalysis results:', error);
    return { data: null, error: `Failed to parse results: ${error.message}` };
  }
};

const FramingDiscourseAnalysis = ({ item }) => {
  if (!item) {
    return <Alert severity="warning">Analysis item data is missing.</Alert>;
  }

  const { analysis_lens_type, results: rawResults } = item;
  const { data: results, error: parseError } = safeParseResults(rawResults);

  if (parseError) {
    return <Alert severity="error">{parseError}</Alert>;
  }

  if (!results) {
    return <Alert severity="warning">Parsed analysis results are empty.</Alert>;
  }

  // Destructure safely with defaults
  const {
    transcripts_analyzed = 0,
    unique_frames = 0,
    dominant_frame = 'N/A',
    frame_prevalence = [],
    top_discursive_strategies = [],
    data_quality_notes = [],
  } = results;

  const renderListItems = (items, icon = null, stripDash = false, variant = 'body2') => {
    if (!items || items.length === 0)
      return (
        <Typography variant="caption" color="text.secondary">
          None specified.
        </Typography>
      );
    return (
      <List dense disablePadding sx={{ pl: 0, mt: 0.5 }}>
        {items.map((text, index) => (
          <ListItem key={index} disableGutters sx={{ pt: 0, pb: 0.5, alignItems: 'flex-start' }}>
            {icon && (
              <ListItemIcon sx={{ minWidth: 'auto', mr: 1, mt: 0.5 }}>
                {React.cloneElement(icon, { fontSize: 'small' })}
              </ListItemIcon>
            )}
            <ListItemText primaryTypographyProps={{ variant: variant, color: 'text.secondary' }}>
              {stripDash && typeof text === 'string' && text.startsWith('- ')
                ? text.substring(2)
                : text}
            </ListItemText>
          </ListItem>
        ))}
      </List>
    );
  };

  const renderQuotes = (quotes) => {
    if (!quotes || quotes.length === 0) return null;
    return (
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
          Representative Quotes:
        </Typography>
        <List dense disablePadding sx={{ pl: 2, borderLeft: '2px solid #ccc' }}>
          {quotes.map((quote, index) => (
            <ListItem key={index} disableGutters sx={{ pt: 0, pb: 0.5 }}>
              <ListItemText
                primaryTypographyProps={{
                  variant: 'caption',
                  fontStyle: 'italic',
                  color: 'text.secondary',
                }}
              >
                {quote}
              </ListItemText>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <AnalysisLayout results={results} item={{ ...item, name: 'Framing & Discourse Analysis' }}>
      <CardHeader
        title={
          <Typography
            variant="h5"
            gutterBottom
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <SpeakerNotesIcon sx={{ verticalAlign: 'bottom', mr: 1 }} />
            Framing & Discourse Analysis
            {analysis_lens_type && (
              <AnalysisLensHelperText lensName={analysis_lens_type} level="run" size="small" />
            )}
          </Typography>
        }
      />
      <Divider />
      <CardContent>
        <Metadata item={item} />
        <Summary item={item} results={results} />
        {/* Counts & Dominant Frame Section */}
        <Grid container spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
          <Grid item xs={6} sm={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{transcripts_analyzed}</Typography>
            <Typography variant="caption">Transcripts Analyzed</Typography>
          </Grid>
          <Grid item xs={6} sm={4} sx={{ textAlign: 'center' }}>
            <Typography variant="h4">{unique_frames}</Typography>
            <Typography variant="caption">Unique Frames Identified</Typography>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ textAlign: 'center' }}>
            <Chip
              icon={<StarIcon />}
              label={`Dominant: ${dominant_frame}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 'medium', p: 1 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Frame Prevalence Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Frame Prevalence ({frame_prevalence.length})
          </Typography>
          {frame_prevalence.length > 0 ? (
            frame_prevalence.map((frame, index) => (
              <Accordion
                key={`frame-${index}`}
                TransitionProps={{ unmountOnExit: true }}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5 }}>
                      <FormatQuoteIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <Typography sx={{ flexGrow: 1, mr: 1, fontWeight: 'medium' }}>
                      {frame.label || 'Unnamed Frame'}
                    </Typography>
                    <Chip label={`Prevalence: ${frame.prevalence || 0}`} size="small" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="body2" paragraph color="text.secondary">
                    {frame.description_summary || 'No description.'}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Common Discursive Strategies:
                  </Typography>
                  {renderListItems(
                    frame.common_discursive_strategies,
                    <CampaignIcon color="action" />,
                    false,
                    'caption',
                  )}
                  {renderQuotes(frame.representative_quotes)}
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No frame prevalence data found.
            </Typography>
          )}
        </Box>

        {/* Top Discursive Strategies Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <CampaignIcon sx={{ verticalAlign: 'bottom', mr: 1, color: 'secondary.main' }} />
            Top Discursive Strategies
          </Typography>
          {top_discursive_strategies.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Strategy</TableCell>
                    <TableCell align="right">Frequency</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {top_discursive_strategies.map((strat, index) => (
                    <TableRow key={index}>
                      <TableCell component="th" scope="row">
                        {strat.strategy}
                      </TableCell>
                      <TableCell align="right">{strat.frequency}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No top discursive strategies identified.
            </Typography>
          )}
        </Box>

        {/* Priority Insights Section */}
        <PriorityInsights results={results} />

        {/* Data Quality Notes Section */}
        <Box>
          <Typography variant="h6" gutterBottom>
            <VerifiedUserIcon sx={{ verticalAlign: 'bottom', mr: 1, color: 'success.main' }} />
            Data Quality Notes
          </Typography>
          {data_quality_notes.length > 0 ? (
            renderListItems(data_quality_notes, <VerifiedUserIcon color="success" />, true)
          ) : (
            <Typography variant="body2" color="text.secondary">
              No data quality notes provided.
            </Typography>
          )}
        </Box>
        <IncludedTranscripts item={item} results={results} />
      </CardContent>
    </AnalysisLayout>
  );
};

FramingDiscourseAnalysis.propTypes = {
  /** The analysis item object containing results and metadata */
  item: PropTypes.shape({
    _id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    study_id: PropTypes.string,
    created_at: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    analysis_lens_type: PropTypes.string,
    results: PropTypes.oneOfType([PropTypes.string, PropTypes.object]), // Can be stringified JSON or parsed object
  }),
};

export default FramingDiscourseAnalysis;
